import type { Compensation, CompensationType, Employee, EmployeeCreate, EmployeeStatus, EmployeeUpdate, WorkSchedule } from '@erp/shared';
import { Pencil, UsersRound, UserX } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Badge, Card, Input, Skeleton } from '../../components/shadcn/index.js';
import { CurrencySelect } from '../../components/ui/CurrencySelect.js';
import {
  Button,
  confirmDialog,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  Money,
} from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { attendanceErrorCode, listWorkSchedules, localToday, setAttendancePin } from '../../lib/attendance.js';
import { normalizeNumericInput } from '../../lib/numerals.js';
import { createCompensation, getCurrentCompensation } from '../../lib/payroll.js';
import { listUsers } from '../../lib/users.js';
import { BulkImportDialog } from '../common/BulkImportDialog.js';

// Columns parsed by the HR bulkImportEmployees controller. Matches
// the field set in EmployeeCreateSchema that hr/hr.controller.ts maps
// from CSV columns. hire_date and default_currency_code are required;
// the rest are optional.
const EMPLOYEES_CSV_TEMPLATE = `code,first_name,last_name,email,phone,hire_date,employment_type,default_currency_code
EMP-001,Asha,Karim,asha@demo.test,+964-770-0000,2025-09-01,full_time,USD
EMP-002,Ravi,Singh,,,,2025-11-15,contractor,USD
`;

const STATUS_VARIANT: Record<EmployeeStatus, 'success' | 'warning' | 'danger' | 'secondary'> = {
  active: 'success',
  on_leave: 'warning',
  suspended: 'warning',
  terminated: 'secondary',
};

const EMPLOYEE_STATUSES: EmployeeStatus[] = ['active', 'on_leave', 'suspended', 'terminated'];

/**
 * What the attendance fields pick from. Each list is loaded independently and
 * degrades to empty on a 403: an HR clerk without users.read still edits the
 * schedule and branch, and simply is not offered the login link.
 */
interface Lookups {
  schedules: WorkSchedule[];
  warehouses: Array<{ id: string; name: string }>;
  users: Array<{ id: string; fullName: string; email: string }> | null;
}

const EMPTY_LOOKUPS: Lookups = { schedules: [], warehouses: [], users: null };

const SELECT_CLS = 'block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg';

type AttendanceFields = Pick<EmployeeUpdate, 'workScheduleId' | 'warehouseId' | 'userId' | 'trackAttendance' | 'deviceUserId'>;

/** The attendance half of the employee form, shared by create and edit. */
function AttendanceFieldset({ value, onChange, lookups }: {
  value: AttendanceFields;
  onChange: (v: AttendanceFields) => void;
  lookups: Lookups;
}): JSX.Element {
  const { t } = useTranslation('hr');
  return (
    <fieldset className="md:col-span-2 grid grid-cols-1 gap-4 rounded-lg border border-border p-4 md:grid-cols-2">
      <legend className="px-1 text-sm font-semibold">{t('employees.attendance.section')}</legend>
      <FormField id="e-schedule" label={t('employees.attendance.schedule')}>
        <select
          id="e-schedule"
          className={SELECT_CLS}
          value={value.workScheduleId ?? ''}
          onChange={(e) => onChange({ ...value, workScheduleId: e.target.value || null })}
        >
          <option value="">{t('employees.attendance.scheduleDefault')}</option>
          {lookups.schedules.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </FormField>
      <FormField id="e-branch" label={t('employees.attendance.branch')}>
        <select
          id="e-branch"
          className={SELECT_CLS}
          value={value.warehouseId ?? ''}
          onChange={(e) => onChange({ ...value, warehouseId: e.target.value || null })}
        >
          <option value="">{t('employees.attendance.noBranch')}</option>
          {lookups.warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
      </FormField>
      {lookups.users && (
        <FormField id="e-user" label={t('employees.attendance.user')}>
          <select
            id="e-user"
            className={SELECT_CLS}
            value={value.userId ?? ''}
            onChange={(e) => onChange({ ...value, userId: e.target.value || null })}
          >
            <option value="">{t('employees.attendance.noUser')}</option>
            {lookups.users.map((u) => <option key={u.id} value={u.id}>{u.fullName} — {u.email}</option>)}
          </select>
        </FormField>
      )}
      <FormField id="e-device" label={t('employees.attendance.deviceId')} helperText={t('employees.attendance.deviceIdHint')}>
        <Input
          id="e-device"
          value={value.deviceUserId ?? ''}
          maxLength={32}
          dir="ltr"
          onChange={(e) => onChange({ ...value, deviceUserId: e.target.value.trim() || null })}
        />
      </FormField>
      <label className="flex items-start gap-2 text-sm md:col-span-2">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 accent-[var(--color-brand)]"
          checked={value.trackAttendance !== false}
          onChange={(e) => onChange({ ...value, trackAttendance: e.target.checked })}
        />
        <span>
          <span className="font-medium">{t('employees.attendance.track')}</span>
          <span className="block text-xs text-fg-muted">{t('employees.attendance.trackHint')}</span>
        </span>
      </label>
    </fieldset>
  );
}

/**
 * The kiosk PIN, set or cleared on its own — never part of the employee save,
 * and never read back: the server only says whether one exists.
 */
function KioskPinField({ employee }: { employee: Employee }): JSX.Element {
  const { t } = useTranslation('hr');
  const [hasPin, setHasPin] = useState(employee.hasAttendancePin);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const valid = /^\d{4,6}$/.test(pin);

  const save = async (next: string | null): Promise<void> => {
    setBusy(true);
    try {
      await setAttendancePin(employee.id, next);
      setHasPin(next !== null);
      setPin('');
      toast.success(t(next ? 'employees.attendance.pinSaved' : 'employees.attendance.pinCleared'));
    } catch {
      toast.error(t('attendance.errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="md:col-span-2 flex flex-wrap items-end gap-2 rounded-lg border border-border p-4">
      <div className="min-w-48 flex-1">
        <FormField
          id="e-pin"
          label={`${t('employees.attendance.pin')} — ${hasPin ? t('employees.attendance.pinSet') : t('employees.attendance.pinNotSet')}`}
          helperText={t('employees.attendance.pinHint')}
        >
          <Input
            id="e-pin"
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            maxLength={6}
            placeholder={t('employees.attendance.pinPlaceholder')}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
          />
        </FormField>
      </div>
      <Button type="button" variant="secondary" loading={busy} disabled={!valid} onClick={() => void save(pin)}>
        {t('employees.attendance.pinSave')}
      </Button>
      {hasPin && (
        <Button type="button" variant="ghost" disabled={busy} onClick={() => void save(null)}>
          {t('employees.attendance.pinClear')}
        </Button>
      )}
    </div>
  );
}

const COMP_TYPES: CompensationType[] = ['salary_monthly', 'salary_annual', 'hourly'];

/**
 * What the employee is paid, today. Payroll takes a salary from here, and
 * skips anybody with none — which, before this field existed, was everybody
 * added from the screen: compensation had an API and no way in.
 *
 * A change is a NEW record from its effective date (the old one closes the day
 * before), so last month's payroll still reads last month's pay.
 */
function SalaryField({ employee }: { employee: Employee }): JSX.Element | null {
  const { t, i18n } = useTranslation('hr');
  const canRead = usePermission('hr.compensation.read');
  const canWrite = usePermission('hr.compensation.write');
  const [current, setCurrent] = useState<Compensation | null | undefined>(undefined);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({
    compType: 'salary_monthly' as CompensationType,
    amount: '',
    currencyCode: employee.defaultCurrencyCode,
    effectiveFrom: localToday(),
  });

  useEffect(() => {
    if (!canRead) return;
    void getCurrentCompensation(employee.id).then(setCurrent).catch(() => setCurrent(null));
  }, [canRead, employee.id]);

  if (!canRead) return null;

  const amount = normalizeNumericInput(draft.amount);
  const valid = /^\d+(\.\d{1,4})?$/.test(amount) && Number(amount) > 0;

  const save = async (): Promise<void> => {
    setBusy(true);
    try {
      const saved = await createCompensation(employee.id, { ...draft, amount });
      // Only what is in force TODAY belongs on the card; a future-dated change
      // waits its turn.
      setCurrent(saved.effectiveFrom <= localToday() ? saved : current ?? null);
      setEditing(false);
      toast.success(t('employees.pay.saved'));
    } catch {
      toast.error(t('attendance.errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="md:col-span-2 rounded-lg border border-border p-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-semibold">{t('employees.pay.section')}</span>
        {current === undefined ? null : current ? (
          <span className="text-sm">
            <Money amount={current.amount} currency={current.currencyCode} locale={i18n.language} />{' '}
            <span className="text-fg-muted">{t(`employees.pay.per.${current.compType}`)} · {t('employees.pay.since', { date: current.effectiveFrom })}</span>
          </span>
        ) : (
          <span className="text-sm text-warning">{t('employees.pay.none')}</span>
        )}
        {canWrite && !editing && (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="ms-auto"
            onClick={() => {
              setDraft({
                compType: current?.compType ?? 'salary_monthly',
                amount: current?.amount ? String(Number(current.amount)) : '',
                currencyCode: current?.currencyCode ?? employee.defaultCurrencyCode,
                effectiveFrom: localToday(),
              });
              setEditing(true);
            }}
          >
            {t('employees.pay.change')}
          </Button>
        )}
      </div>
      {editing && (
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-4">
          <FormField id="pay-type" label={t('employees.pay.type')}>
            <select
              id="pay-type"
              className={SELECT_CLS}
              value={draft.compType}
              onChange={(e) => setDraft({ ...draft, compType: e.target.value as CompensationType })}
            >
              {COMP_TYPES.map((c) => <option key={c} value={c}>{t(`employees.pay.types.${c}`)}</option>)}
            </select>
          </FormField>
          <FormField id="pay-amount" label={t('employees.pay.amount')} required>
            <Input id="pay-amount" inputMode="decimal" value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} />
          </FormField>
          <FormField id="pay-currency" label={t('employees.pay.currency')} required>
            <CurrencySelect id="pay-currency" value={draft.currencyCode} onChange={(c) => setDraft({ ...draft, currencyCode: c })} required />
          </FormField>
          <FormField id="pay-from" label={t('employees.pay.effectiveFrom')} required>
            <Input id="pay-from" type="date" value={draft.effectiveFrom} onChange={(e) => setDraft({ ...draft, effectiveFrom: e.target.value })} />
          </FormField>
          {draft.compType === 'hourly' && (
            <p className="md:col-span-4 text-xs text-fg-muted">{t('employees.pay.hourlyHint')}</p>
          )}
          <div className="md:col-span-4 flex gap-2">
            <Button type="button" size="sm" loading={busy} disabled={!valid || !draft.effectiveFrom} onClick={() => void save()}>
              {t('employees.pay.save')}
            </Button>
            <Button type="button" size="sm" variant="secondary" disabled={busy} onClick={() => setEditing(false)}>
              {t('employees.edit.cancel')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** A refusal the attendance fields can cause, as a sentence; null for anything else. */
function linkError(err: unknown, t: (k: string) => string): string | null {
  const code = attendanceErrorCode(err);
  if (code === 'user_already_linked') return t('employees.errors.userAlreadyLinked');
  if (code === 'device_id_taken') return t('employees.errors.deviceIdTaken');
  return null;
}

export function EmployeesPage(): JSX.Element {
  const { t } = useTranslation('hr');
  const { t: tc } = useTranslation();
  const canWrite = usePermission('hr.employees.write');
  const [items, setItems] = useState<Employee[] | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Employee | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [lookups, setLookups] = useState<Lookups>(EMPTY_LOOKUPS);

  const reload = async (): Promise<void> => {
    const r = await api().get<{ items: Employee[] }>('/hr/employees');
    setItems(r.data.items);
  };

  useEffect(() => {
    void reload();
  }, []);

  useEffect(() => {
    if (!canWrite) return;
    void Promise.all([
      listWorkSchedules().catch(() => []),
      api().get<{ items: Array<{ id: string; name: string }> }>('/warehouses').then((r) => r.data.items).catch(() => []),
      listUsers().then((us) => us.map((u) => ({ id: u.id, fullName: u.fullName, email: u.email }))).catch(() => null),
    ]).then(([schedules, warehouses, users]) => setLookups({ schedules, warehouses, users }));
  }, [canWrite]);

  if (!items) {
    return (
      <Card className="p-6">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-4 h-32 w-full" />
      </Card>
    );
  }

  const openEdit = (emp: Employee): void => {
    setShowCreate(false);
    setEditing(emp);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onTerminate = async (emp: Employee): Promise<void> => {
    if (!(await confirmDialog({ message: t('employees.actions.confirmTerminate', { code: emp.code, name: emp.displayName }), tone: 'danger' }))) return;
    try {
      // DELETE /hr/employees/:id flips status to 'terminated' on the server.
      // We don't surface terminationDate / terminationReason here — those
      // are deeper HR-process fields and belong in a future "termination
      // dialog" if requirements grow.
      await api().delete(`/hr/employees/${emp.id}`);
      await reload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };

  const columns: DataTableColumn<Employee>[] = [
    {
      id: 'code',
      header: t('employees.columns.code'),
      sortValue: (e) => e.code,
      searchValue: (e) => e.code,
      cellClassName: 'font-mono text-xs',
      cell: (e) => e.code,
    },
    {
      id: 'name',
      header: t('employees.columns.name'),
      sortValue: (e) => e.displayName,
      searchValue: (e) => e.displayName,
      cellClassName: 'font-medium',
      cell: (e) => e.displayName,
    },
    {
      id: 'email',
      header: t('employees.columns.email'),
      sortValue: (e) => e.email ?? '',
      searchValue: (e) => e.email ?? '',
      cellClassName: 'text-fg-muted',
      cell: (e) => e.email ?? '—',
    },
    {
      id: 'hireDate',
      header: t('employees.columns.hireDate'),
      sortValue: (e) => e.hireDate,
      cellClassName: 'font-mono text-xs',
      cell: (e) => e.hireDate,
    },
    {
      id: 'status',
      header: t('employees.columns.status'),
      sortValue: (e) => t(`employees.status.${e.status}`),
      searchValue: (e) => t(`employees.status.${e.status}`),
      cell: (e) => (
        <Badge variant={STATUS_VARIANT[e.status]}>
          {t(`employees.status.${e.status}`)}
        </Badge>
      ),
    },
    ...(canWrite
      ? [
          {
            id: 'actions',
            header: '',
            align: 'end' as const,
            headClassName: 'w-32',
            cell: (e: Employee) => (
              <div className="flex justify-end gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openEdit(e)}
                  aria-label={`${t('employees.actions.edit')} ${e.code}`}
                  title={t('employees.actions.edit')}
                >
                  <Pencil className="h-4 w-4" aria-hidden />
                </Button>
                {e.status !== 'terminated' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onTerminate(e)}
                    aria-label={`${t('employees.actions.terminate')} ${e.code}`}
                    title={t('employees.actions.terminate')}
                  >
                    <UserX className="h-4 w-4" aria-hidden />
                  </Button>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('employees.title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('employees.description')}</p>
        </div>
        {canWrite && !showCreate && !editing && (
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setShowImport(true)}>
              {tc('bulkImport.button', { defaultValue: 'Import CSV' })}
            </Button>
            <Button onClick={() => setShowCreate(true)}>{t('employees.create.title')}</Button>
          </div>
        )}
      </header>

      {showImport && (
        <BulkImportDialog
          title={tc('bulkImport.titleEmployees', { defaultValue: 'Import employees' })}
          endpoint="/hr/employees/bulk-import"
          templateCsv={EMPLOYEES_CSV_TEMPLATE}
          templateName="employees-template.csv"
          onDone={() => { setShowImport(false); void reload(); }}
          onCancel={() => setShowImport(false)}
        />
      )}

      {showCreate && (
        <CreateEmployeeForm
          lookups={lookups}
          onCancel={() => setShowCreate(false)}
          onCreated={async () => {
            setShowCreate(false);
            await reload();
          }}
        />
      )}

      {editing && (
        <EditEmployeeForm
          employee={editing}
          lookups={lookups}
          onCancel={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await reload();
          }}
        />
      )}

      {items.length === 0 && !showCreate ? (
        <EmptyState
          icon={<UsersRound className="h-8 w-8" aria-hidden />}
          title={t('employees.empty')}
          description={t('employees.emptyDescription')}
          action={
            canWrite ? (
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button onClick={() => setShowCreate(true)}>{t('employees.create.title')}</Button>
                <Button variant="secondary" onClick={() => setShowImport(true)}>
                  {tc('bulkImport.button', { defaultValue: 'Import CSV' })}
                </Button>
              </div>
            ) : undefined
          }
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable
            columns={columns}
            data={items}
            rowKey={(e) => e.id}
            searchPlaceholder={t('employees.searchPlaceholder')}
          />
        </Card>
      )}
    </div>
  );
}

interface CreateFormProps { lookups: Lookups; onCancel: () => void; onCreated: () => void | Promise<void>; }

function CreateEmployeeForm({ lookups, onCancel, onCreated }: CreateFormProps): JSX.Element {
  const { t } = useTranslation('hr');
  const { t: tc } = useTranslation();
  const [form, setForm] = useState<EmployeeCreate>({
    code: '',
    firstName: '',
    lastName: '',
    hireDate: new Date().toISOString().slice(0, 10),
    defaultCurrencyCode: 'USD',
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api().post('/hr/employees', form);
      await onCreated();
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } }).response?.data?.code;
      setError(linkError(err, t) ?? (code === 'conflict' ? t('employees.errors.duplicateCode') : tc('errors:server')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6">
      <h2 className="mb-4 text-lg font-semibold">{t('employees.create.title')}</h2>
      <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-2" noValidate>
        <FormField id="e-code" label={t('employees.create.code')} required>
          <Input id="e-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required autoFocus />
        </FormField>
        <FormField id="e-first" label={t('employees.create.firstName')} required>
          <Input id="e-first" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} required />
        </FormField>
        <FormField id="e-last" label={t('employees.create.lastName')} required>
          <Input id="e-last" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} required />
        </FormField>
        <FormField id="e-email" label={t('employees.create.email')}>
          <Input id="e-email" type="email" value={form.email ?? ''} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </FormField>
        <FormField id="e-hire" label={t('employees.create.hireDate')} required>
          <Input id="e-hire" type="date" value={form.hireDate} onChange={(e) => setForm({ ...form, hireDate: e.target.value })} required />
        </FormField>
        <FormField id="e-currency" label={t('employees.create.currency')} required>
          <CurrencySelect id="e-currency" value={form.defaultCurrencyCode} onChange={(c) => setForm({ ...form, defaultCurrencyCode: c })} required />
        </FormField>
        <AttendanceFieldset value={form} onChange={(v) => setForm({ ...form, ...v })} lookups={lookups} />
        {error && <p className="md:col-span-2 text-sm text-danger" role="alert">{error}</p>}
        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" loading={saving}>{tc('actions.create')}</Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>{tc('actions.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------------------

interface EditEmployeeFormProps {
  employee: Employee;
  lookups: Lookups;
  onCancel: () => void;
  onSaved: () => void | Promise<void>;
}

function EditEmployeeForm({ employee, lookups, onCancel, onSaved }: EditEmployeeFormProps): JSX.Element {
  const { t } = useTranslation('hr');
  const { t: tc } = useTranslation();
  // EmployeeUpdateSchema doesn't accept `code` (immutable identifier) or
  // `hireDate` (historical anchor — once an employee is hired, that date
  // is locked). Both stay read-only in the form. status IS in the update
  // schema, so we expose it as a dropdown — useful for flipping someone
  // to on_leave / suspended without going through the dedicated
  // termination action. Picking 'terminated' here also works but the
  // per-row Terminate button is the encouraged path.
  const [form, setForm] = useState<EmployeeUpdate>({
    firstName: employee.firstName,
    lastName: employee.lastName,
    email: employee.email,
    status: employee.status,
    defaultCurrencyCode: employee.defaultCurrencyCode,
    workScheduleId: employee.workScheduleId,
    warehouseId: employee.warehouseId,
    userId: employee.userId,
    trackAttendance: employee.trackAttendance,
    deviceUserId: employee.deviceUserId,
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const patch: EmployeeUpdate = {
        firstName: form.firstName?.trim() || undefined,
        lastName: form.lastName?.trim() || undefined,
        email: form.email && form.email.trim() ? form.email.trim() : null,
        status: form.status,
        defaultCurrencyCode: form.defaultCurrencyCode,
        workScheduleId: form.workScheduleId ?? null,
        warehouseId: form.warehouseId ?? null,
        trackAttendance: form.trackAttendance,
        deviceUserId: form.deviceUserId ?? null,
        // Only sent when the picker was offered: without users.read the field is
        // absent, and sending null would silently unlink whoever was linked.
        ...(lookups.users ? { userId: form.userId ?? null } : {}),
      };
      await api().patch(`/hr/employees/${employee.id}`, patch);
      await onSaved();
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } }).response?.data?.code;
      setError(linkError(err, t) ?? (code === 'unprocessable' ? t('employees.errors.unprocessable') : tc('errors:server')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6">
      <h2 className="mb-4 text-lg font-semibold">
        {t('employees.edit.title', { code: employee.code, name: employee.displayName })}
      </h2>
      <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-2" noValidate>
        <FormField id="ee-code" label={t('employees.create.code')} required>
          <Input
            id="ee-code"
            value={employee.code}
            readOnly
            aria-readonly
            className="cursor-not-allowed bg-bg-muted text-fg-muted"
          />
        </FormField>
        <FormField id="ee-first" label={t('employees.create.firstName')} required>
          <Input
            id="ee-first"
            value={form.firstName ?? ''}
            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
            required
            autoFocus
          />
        </FormField>
        <FormField id="ee-last" label={t('employees.create.lastName')} required>
          <Input
            id="ee-last"
            value={form.lastName ?? ''}
            onChange={(e) => setForm({ ...form, lastName: e.target.value })}
            required
          />
        </FormField>
        <FormField id="ee-email" label={t('employees.create.email')}>
          <Input
            id="ee-email"
            type="email"
            value={form.email ?? ''}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </FormField>
        <FormField id="ee-hire" label={t('employees.create.hireDate')} required>
          <Input
            id="ee-hire"
            type="date"
            value={employee.hireDate}
            readOnly
            aria-readonly
            className="cursor-not-allowed bg-bg-muted text-fg-muted"
          />
        </FormField>
        <FormField id="ee-currency" label={t('employees.create.currency')} required>
          <CurrencySelect
            id="ee-currency"
            value={form.defaultCurrencyCode ?? ''}
            onChange={(c) => setForm({ ...form, defaultCurrencyCode: c })}
            required
          />
        </FormField>
        <FormField id="ee-status" label={t('employees.edit.status')}>
          <select
            id="ee-status"
            className="block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
            value={form.status ?? 'active'}
            onChange={(e) => setForm({ ...form, status: e.target.value as EmployeeStatus })}
          >
            {EMPLOYEE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`employees.status.${s}`)}
              </option>
            ))}
          </select>
        </FormField>
        <AttendanceFieldset value={form} onChange={(v) => setForm({ ...form, ...v })} lookups={lookups} />
        <KioskPinField employee={employee} />
        <SalaryField employee={employee} />
        {error && <p className="md:col-span-2 text-sm text-danger" role="alert">{error}</p>}
        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" loading={saving}>{t('employees.edit.submit')}</Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
            {t('employees.edit.cancel')}
          </Button>
        </div>
      </form>
    </Card>
  );
}
